/**
 * Analytics (docs/analytics.md §2, Studio Console): tabs Overview · Content · Audience · Live (brands
 * that do live) · Money (brands that sell something or give access), and the date range above every
 * report, both kept in the URL. Each tab is its own screen (src/analytics/screens); this page only
 * chooses one and hands it the range.
 */

import Tabs from "@web-ui/components/Tabs";
import { RangePicker } from "@/analytics/components/range-picker";
import { useMoneyShown } from "@/analytics/money-shown";
import { parseRange } from "@/analytics/range";
import { useReportScope } from "@/analytics/scope";
import { Audience } from "@/analytics/screens/audience";
import { Content } from "@/analytics/screens/content";
import { Live } from "@/analytics/screens/live";
import { Money } from "@/analytics/screens/money";
import { Overview } from "@/analytics/screens/overview";
import { go, type AnalyticsTab, type Route } from "@/lib/route";
import { showsLive } from "@/shell/navigation";
import { useStudio } from "@/shell/studio";

const NAMES: Record<AnalyticsTab, string> = { overview: "Overview", content: "Content", audience: "Audience", live: "Live", money: "Money" };

/** The tabs this brand has. */
export function analyticsTabs(live: boolean, money: boolean): AnalyticsTab[] {
  return ["overview", "content", "audience", ...(live ? ["live" as const] : []), ...(money ? ["money" as const] : [])];
}

export function Analytics({ route }: { route: Route }) {
  const { brandView, counts } = useStudio();
  const money = useMoneyShown();
  const days = parseRange(route.range);
  const scope = useReportScope(days);
  const tabs = analyticsTabs(showsLive(brandView?.manifest.features, counts), !!money);
  // A tab this brand does not have (a shared link, live switched off) shows the Overview.
  const asked = route.report ?? "overview";
  const tab = tabs.includes(asked) || (asked === "money" && money === null) || (asked === "live" && !brandView) ? asked : "overview";
  const items = tabs.map((t, i) => ({ id: i, name: NAMES[t] }));

  return (
    <>
      <nav aria-label="Analytics" className="-mx-1 overflow-x-auto px-1 scrollbar-none">
        <Tabs className="w-max" items={items} value={items[Math.max(0, tabs.indexOf(tab))]!} setValue={(o) => go({ brand: scope.brand, section: "analytics", report: tabs[o.id], range: days })} />
      </nav>
      <RangePicker value={days} onChange={(next) => go({ ...route, range: next })} />
      {tab === "overview" ? <Overview scope={scope} /> : null}
      {tab === "content" ? <Content scope={scope} /> : null}
      {tab === "audience" ? <Audience scope={scope} /> : null}
      {tab === "live" ? <Live scope={scope} /> : null}
      {tab === "money" ? <Money scope={scope} /> : null}
    </>
  );
}
